import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExperiences } from 'core/contexts/experienceV2';
import { useExperienceWizard } from 'core/contexts/experienceWizard';
import { Field, Form } from 'react-final-form';
import { Box, Button, Grid2 } from '@mui/material';
import Title from 'modules/Pricing/components/Title';
import ActionButtons from 'modules/Pricing/PriceGroupWizard/components/ActionButtons';
import { Container, Label } from '../../styles';
import { AlertDialog, CharacterCounter, Checklist, Switch, Textarea } from '@utriper/core-ui';
import { environmental_policies, getEnvironmentalIds, getTranslatedName } from '@utriper/core';
import { Add } from '@mui/icons-material';
import AddIncludesOptionForm from '../AddIncludesOptionForm';
import { toast } from 'react-toastify';

const EcoMessageForm = () => {
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`experience_form.${key}`);
    const history = useHistory();
    const { stepData, handleBack, updateStepData, STEP_KEYS, resetWizard, submitting, addExperience, setIsSuccess } = useExperienceWizard();
    const { putExperienceMedia } = useExperiences();
    const defaultEnvOptions = useMemo(
        () =>
            getEnvironmentalIds()
                .slice(0, 4)
                .map((id) => ({
                    id,
                    label: getTranslatedName(environmental_policies, id, i18n.language),
                })),
        [i18n.language]
    );
    const [environmentOptions, setEnvironmentOptions] = useState([...defaultEnvOptions]);
    const [openAdd, setOpenAdd] = useState(false);

    const handleCloseForm = () => setOpenAdd(false);

    const handleAddOption = ({ newOption }) => {
        if (!newOption) return;

        const envOption = {
            id: 5, // other
            label: newOption,
        };

        setEnvironmentOptions((prev) => [...prev, envOption]);
    };

    const handleSubmit = async (values) => {
        const formData = { ...values, environmentOptions };
        updateStepData(STEP_KEYS.STEP_7, formData);
        const result = await addExperience({ ...stepData, [STEP_KEYS.STEP_7]: formData });

        if (result?.id) {
            setIsSuccess(true);
            toast.success(translate('success_creation'), {
                position: 'top-right',
                theme: 'colored',
            });
            const experienceImages = stepData?.step4?.images || [];
            if (experienceImages?.length > 0) {
                await putExperienceMedia(result.id, experienceImages);
            }
            resetWizard();
        } else {
            // history.push('/experience');
        }
    };

    const validateForm = (values) => {
        const errors = {};

        return errors;
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validateForm}
            initialValues={stepData[STEP_KEYS.STEP_7]}
            render={({ handleSubmit, values }) => (
                <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: '480px' }}>
                    <Box component="section">
                        <Title title={translate('eco_message.title')} description={translate('eco_message.description')} />

                        {/* Form */}
                        <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                            {/* Pax Message */}
                            <Container>
                                <Label>{translate('pax_message')}</Label>
                                <Field
                                    name="paxMessage"
                                    component={Textarea}
                                    placeholder={translate('pax_message_placeholder')}
                                    minRows={4}
                                    slotProps={{
                                        input: {
                                            inputProps: {
                                                maxLength: 500,
                                            },
                                        },
                                    }}
                                />
                                <CharacterCounter length={values?.paxMessage?.length ?? 0} maxLength={500} />
                            </Container>

                            {/* Require ID */}
                            <Container
                                sx={{
                                    '& .MuiSwitch-switchBase': {
                                        '&.Mui-checked': {
                                            color: '#fff',
                                            '& + .MuiSwitch-track': {
                                                backgroundColor: '#137DC5',
                                                opacity: 1,
                                            },
                                        },
                                    },
                                }}
                            >
                                <Field name="requireId" type="checkbox" component={Switch} label={translate('check_in')} />
                            </Container>

                            {/* Environmental Policies */}
                            <Container>
                                <Label>{translate('env_policies')}</Label>
                                <Field name="envPolicies" component={Checklist} options={environmentOptions} />
                                {environmentOptions?.length < 5 && (
                                    <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={() => setOpenAdd(true)}>
                                        {translate('add_new_option')}
                                    </Button>
                                )}
                            </Container>
                        </Grid2>

                        <ActionButtons
                            handleSubmit={(e) => {
                                return handleSubmit(e);
                            }}
                            handleBack={handleBack}
                            submitting={submitting}
                        />
                    </Box>

                    {/* Add Dialog */}
                    <AlertDialog open={openAdd} handleClose={handleCloseForm} hideTitle hideActions maxWidth="sm" fullWidth dialogContent={<AddIncludesOptionForm onAddOption={handleAddOption} onClose={handleCloseForm} maxLength={255} placeholder={translate('env_policies_placeholder')} />} />
                </form>
            )}
        />
    );
};

export default EcoMessageForm;
